import { FC, useEffect, useState } from "react";
import { Score } from "@/models/pdfData";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  PaperOwnProps,
  TextField,
  Typography,
} from "@mui/material";
import { useGameDataContext } from "@context/GameDataContext";
import { useSelectedChallengeContext } from "@context/SelectedChallengeContext";

const ScoreSelector: FC<{
  title: string;
  setScore: (score: Score) => void;
  score?: Score;
  disabled?: boolean;
  greyedOut?: boolean;
}> = ({ score, setScore, title, disabled = false, greyedOut = false }) => {
  const {
    challenge: { team },
  } = useSelectedChallengeContext();
  const { game } = useGameDataContext();
  const team1 = game?.gameData?.team1 || {
    iconUrl: null,
    clubCode: "A",
  };
  const team2 = game?.gameData?.team2 || {
    iconUrl: null,
    clubCode: "B",
  };

  const firstTeam = team === "team1" ? team1 : team2;
  const secondTeam = team === "team1" ? team2 : team1;

  const [scoreFirstTeam, setScoreFirstTeam] = useState<number | undefined>(
    score?.[team === "team1" ? "team1" : "team2"],
  );
  const [scoreSecondTeam, setScoreSecondTeam] = useState<number | undefined>(
    score?.[team === "team1" ? "team2" : "team1"],
  );
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (focused) {
      if (scoreFirstTeam && scoreSecondTeam) {
        setScore({
          team1: team === "team1" ? scoreFirstTeam : scoreSecondTeam,
          team2: team === "team1" ? scoreSecondTeam : scoreFirstTeam,
        });
      }
    } else {
      if (score) {
        setScoreFirstTeam(score?.[team === "team1" ? "team1" : "team2"]);
        setScoreSecondTeam(score?.[team === "team1" ? "team2" : "team1"]);
      }
    }
  }, [score, scoreFirstTeam, scoreSecondTeam]);

  const opacity = greyedOut ? 0.2 : disabled ? 0.6 : 1;
  const variant: PaperOwnProps["variant"] = greyedOut
    ? "outlined"
    : "elevation";
  const elevation: PaperOwnProps["elevation"] =
    greyedOut || disabled ? undefined : 5;

  const TeamIcon: FC<{ src: string }> = ({ src }) => {
    return (
      <Box
        maxWidth={"100px"}
        sx={{
          objectFit: "contain",
          boxShadow: "black",
          filter: "drop-shadow(0px 0px 4px #0002)",
        }}
      >
        <img
          alt="Team Icon"
          src={src}
          style={{
            maxWidth: "100%",
            height: "auto",
          }}
        />
      </Box>
    );
  };

  return (
    <Card
      variant={variant}
      sx={{ width: "350px", mx: 2 }}
      elevation={elevation}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      <CardHeader
        title={title}
        titleTypographyProps={{ variant: "subtitle1", align: "center" }}
        sx={{ pb: 0, opacity }}
      />
      <CardContent>
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          gap={1}
        >
          <Box flexShrink={1} display={"flex"}>
            {firstTeam.iconUrl ? (
              <TeamIcon src={firstTeam.iconUrl} />
            ) : (
              <Typography>{firstTeam.clubCode}</Typography>
            )}
          </Box>
          <Box flexShrink={1} minWidth={"50px"}>
            <TextField
              id="team1Score"
              label={firstTeam.clubCode}
              variant="outlined"
              type={"text"}
              autoComplete="off"
              value={
                !greyedOut && (scoreFirstTeam || scoreFirstTeam === 0)
                  ? scoreFirstTeam
                  : ""
              }
              onChange={(event) =>
                setScoreFirstTeam(
                  Number.parseInt(
                    /\d{0,2}/.exec(event.target.value)?.[0] || "0",
                  ),
                )
              }
              disabled={disabled || greyedOut}
            />
          </Box>
          <Box flexShrink={0}>
            <Typography align={"center"}>:</Typography>
          </Box>
          <Box flexShrink={1} minWidth={"50px"}>
            <TextField
              id="team2Score"
              label={secondTeam.clubCode}
              variant="outlined"
              type={"text"}
              autoComplete="off"
              value={
                !greyedOut && (scoreSecondTeam || scoreSecondTeam === 0)
                  ? scoreSecondTeam
                  : ""
              }
              onChange={(event) =>
                setScoreSecondTeam(
                  Number.parseInt(
                    /\d{0,2}/.exec(event.target.value)?.[0] || "0",
                  ),
                )
              }
              disabled={disabled || greyedOut}
            />
          </Box>
          <Box flexShrink={1} display={"flex"}>
            {secondTeam.iconUrl ? (
              <TeamIcon src={secondTeam.iconUrl} />
            ) : (
              <Typography>{secondTeam.clubCode}</Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ScoreSelector;
